import { useId, useRef } from 'react';
import { CircleHelp } from 'lucide-react';

/** Kort zacht piepje — valt stil als audio niet mag. */
function playTipChime() {
  if (typeof window === 'undefined') return;
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 920;
    o.connect(g);
    g.connect(ctx.destination);
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.06, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
    o.start(t0);
    o.stop(t0 + 0.11);
    o.onended = () => void ctx.close();
  } catch {
    /* ignore */
  }
}

const CHIME_MIN_MS = 720;

/**
 * Uitleg bij hover (of toetsenbord-focus): zichtbare tooltip.
 * Zacht geluid alleen bij aanwijzen met de muis — niet bij klik of tab.
 */
export function HelpTip({ text }: { text: string }) {
  const id = useId();
  const lastChime = useRef(0);

  function onMouseEnter() {
    const now = Date.now();
    if (now - lastChime.current < CHIME_MIN_MS) return;
    lastChime.current = now;
    playTipChime();
  }

  return (
    <span className="group relative ml-1 inline-flex align-middle">
      <button
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-ct-border bg-ct-elevated text-ct-accent transition hover:border-ct-accent/50 hover:bg-ct-accent/15"
        aria-describedby={id}
        onMouseEnter={onMouseEnter}
      >
        <CircleHelp className="h-4 w-4" strokeWidth={2} aria-hidden />
        <span className="sr-only">Uitleg</span>
      </button>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 z-[60] w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-ct-border bg-ct-elevated px-3 py-2.5 text-left text-xs leading-relaxed text-ct-text opacity-0 shadow-lg transition-all duration-200 ease-out [visibility:hidden] group-hover:opacity-100 group-hover:[visibility:visible] group-focus-within:opacity-100 group-focus-within:[visibility:visible]"
      >
        {text}
      </span>
    </span>
  );
}
